/**
 * Hook para buscar estatísticas de execução de fluxos do tenant
 * Retorna: execuções hoje, taxa de conclusão e quantidade de erros
 * 
 * SEGURANÇA: tenant_id é obtido internamente via auth.uid() no backend
 * Não é mais necessário passar tenant_id como parâmetro
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Interface para as estatísticas retornadas
interface FlowExecutionStats {
  executions: number;
  completionRate: number;
  errors: number;
}

/**
 * Hook que busca estatísticas de execução de fluxos do tenant atual
 * O tenant é obtido automaticamente no backend via auth.uid()
 * @returns Query com as estatísticas de execução
 */
export function useFlowExecutionStats() {
  return useQuery({
    queryKey: ['flow-execution-stats'],
    queryFn: async (): Promise<FlowExecutionStats> => {
      logger.debug('useFlowExecutionStats', 'Buscando estatísticas (tenant obtido no backend)');
      
      // RPC obtém tenant internamente via get_validated_tenant_id()
      // Cast necessário pois types.ts ainda não foi atualizado após migration
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('get_tenant_flow_execution_stats', {});
      
      if (error) {
        logger.error('useFlowExecutionStats', 'Erro ao buscar estatísticas', error.message);
        throw error;
      }
      
      // A RPC retorna um array com uma linha
      const stats = data?.[0] as { executions_today: number; completed_count: number; failed_count: number; completion_rate: number } | undefined;
      
      logger.debug('useFlowExecutionStats', 'Estatísticas recebidas', JSON.stringify(stats));
      
      return {
        executions: Number(stats?.executions_today) || 0,
        completionRate: Number(stats?.completion_rate) || 0,
        errors: Number(stats?.failed_count) || 0,
      };
    },
    refetchInterval: 30000, // Atualiza a cada 30 segundos
    staleTime: 10000, // Considera fresh por 10 segundos
  });
}
